import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation'
import { getVoteStatus, getPtp, postPtp } from 'src/api';
import { useUser } from 'src/context';
import { usePoster } from '../PosterContext';

const POSTER_SRC = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/poster2.png";
const POSTER_TITLE = "被詛咒的武士，轉生至雨之神域";
const POSTER_DESC = "演技炸裂 感人肺腑";

const VARIANT_SRC = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/poster2v.jpg";
const VARIANT_DESC = "俠義虛名 心火貪婪";

const PosterModal2 = ({ isOpen, onClose }) => {
  const [showVariant, setShowVariant] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { isGameEnabled } = usePoster();

  const { isLogin } = useUser();

  const { data: ptpData, isLoading: ptpLoading, mutate: ptpMutate } = useSWR(isLogin ? `goty2024-ptp` : null, getPtp);

  const { trigger, isMutating } = useSWRMutation(
    isLogin ? `goty2024-ptp` : null, 
    (url, { arg }) => postPtp(arg),
    {
      onSuccess: (res) => {
        ptpMutate();
      },
    }
  );

  const hasCoin = ptpData?.data?.coin === 1 || false;
  const isCoinGiven = ptpData?.data?.coin === 2 || false;

  useEffect(() => {
    if (isGameEnabled) {
      const img = new Image();
      img.src = VARIANT_SRC;
    }
  }, [isGameEnabled]);

  useEffect(() => {
    if (isGameEnabled && isCoinGiven) {
      setShowVariant(true);
    }
  }, [isGameEnabled, isCoinGiven]);

  const handleImageClick = () => {
    if (isGameEnabled && !isCoinGiven) {
      setShowDialog(true);
    }
  };

  const handleDialogFinish = () => {
    setShowDialog(false);
    setShowVariant(true);
    trigger({
      behavior: "GIVE_COIN",
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center transition-all"
      style={{
        backgroundImage: `url('/img/goty2024/hero-bricks.png')`,
        backgroundRepeat: 'repeat',
        backgroundColor: 'rgba(0,0,0,0.90)',
      }}
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white text-3xl font-bold z-60"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="關閉"
      >
        ×
      </button>
      <div
        className="flex flex-col items-center justify-between relative"
        onClick={e => e.stopPropagation()}
      >
        <div
          className="w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] bg-[#DFB750] text-center border-x-8 border-b-8 border-solid border-black mr-[-1rem] ml-[-1rem] md:mr-[-2rem] md:ml-[-2rem]"
        >
          <h2 className="text-2xl md:text-3xl font-bold tracking-wide text-black py-2 md:py-4">
            {POSTER_TITLE}
          </h2>
        </div>
        <div className="relative w-80 md:w-auto h-auto md:h-[75vh]">
          {isGameEnabled ? (
            <div className="relative w-full h-full">
              <img src={POSTER_SRC} alt="" className="object-contain w-full h-full mx-auto border-x-8 border-b-8 border-solid border-black shadow-2xl invisible" />
              <img src={VARIANT_SRC} alt="Variant Poster" className="absolute inset-0 object-contain w-full h-full mx-auto border-x-8 border-b-8 border-solid border-black shadow-2xl z-10" />
              <img
                src={POSTER_SRC}
                alt={POSTER_TITLE}
                className={clsx(
                  "absolute inset-0 object-contain w-full h-full mx-auto border-x-8 border-b-8 border-solid border-black shadow-2xl z-20 transition-opacity duration-500",
                  { 'opacity-0': showVariant },
                  { 'cursor-pointer': isGameEnabled && !isCoinGiven }
                )}
                onClick={handleImageClick}
              />
            </div>
          ) : (
            <img src={POSTER_SRC} alt={POSTER_TITLE} className="object-contain w-full h-full mx-auto border-x-8 border-b-8 border-solid border-black shadow-2xl" />
          )}
        </div>
        <div className="mt-6 text-center">
          <p className={clsx("text-xl md:text-2xl font-bold tracking-widest shadow-2xl", {
            'text-white': !isGameEnabled,
            'text-black': isGameEnabled
          })}>
            {isGameEnabled ? <>俠義虛名 心<span className="text-white">火</span>貪婪</> : POSTER_DESC}
          </p>
        </div>
      </div>

      {/* Dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="flex flex-col items-center justify-center relative w-full max-w-xs md:max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-full flex flex-col justify-center">
              <div className="relative bg-white p-4 md:p-6 rounded-lg shadow-md mb-4">
                <div className="text-black text-center">
                  <div className="mb-4">
                    {hasCoin && (
                      <img 
                        src="/img/goty2024/game-coin.png" 
                        alt="金幣" 
                        className="w-16 h-16 mx-auto mb-3 object-contain"
                      />
                    )}
                    <h3 className="text-lg md:text-xl font-bold mb-2">「{hasCoin ? "你身上有錢！快把錢交出來" : "此路是我開，此樹是我栽！若要過此路，留下買路財！"}」</h3>
                    <p className="text-sm md:text-base">
                      {hasCoin ? <>你靈機一動把錢拋進到他身後的池塘<br/>
                        他為了撿錢跳進池塘，撲通一聲濺起了超大水花<br/>
                        他會不會游泳啊...？先不管那麼多了</> 
                        : <>你身上沒什麼值錢的東西給他<br/>決定先離他遠點</>
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <button
                  onClick={hasCoin ? handleDialogFinish : () => setShowDialog(false)}
                  className="w-full p-3 md:p-4 text-center border-2 border-[#7B2337] rounded-lg transition-all duration-300 bg-white text-[#7B2337] hover:bg-[#7B2337] hover:text-white font-bold"
                >
                  {hasCoin ? "四處看看" : "四處逛逛"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosterModal2;
